import { gql } from '@apollo/client';

// ── Shared fragments ────────────────────────────────────────────────────────────

// Shared full CallSession field set. AppSync subscription payloads are bounded
// by the publishing mutation's selection set (round-8 finding) — if any of
// the publisher mutations select a slim subset, subscribers receive a
// partial CallSession that nulls non-null fields and corrupts the drawer
// snapshot. Every mutation listed in @aws_subscribe(mutations: [...]) for
// onCallStateUpdate MUST select this fragment, and the subscription/queries
// reuse it so they cannot drift out of sync.
export const CALL_SESSION_FRAGMENT = gql`
  fragment CallSessionFields on CallSession {
    callId
    direction
    provider
    status
    fromNumber
    toNumber
    authState
    authUserId
    authUsername
    authDisplayName
    authAttempts
    lockoutUntil
    transferState
    transferReason
    transcriptEnabled
    recordingEnabled
    retentionDays
    createdAt
    updatedAt
    endedAt
  }
`;

// ── Queries ─────────────────────────────────────────────────────────────────────

export const GET_MY_PROFILE = gql`
  query GetMyProfile {
    getMyProfile {
      username
      email
      displayName
      roles
      permissions
      authProvider
      createdAt
      updatedAt
      lastLogin
      lastSeenAt
      presenceOverride
      statusMessage
      notificationSoundEnabled
      notificationSoundType
      composerToolbarVisible
      quickReactChips
    }
  }
`;

export const LIST_USERS = gql`
  query ListUsers {
    listUsers {
      username
      email
      displayName
      roles
      permissions
      authProvider
      createdAt
      updatedAt
      lastLogin
      lastSeenAt
      presenceOverride
      statusMessage
    }
  }
`;

export const LIST_MY_CONVERSATIONS = gql`
  query ListMyConversations {
    listMyConversations {
      conversation {
        id
        title
        viewerTitle
        viewerCounterpartId
        type
        aiEnabled
        creatorId
        description
        memberCount
        createdAt
        updatedAt
      }
      membership {
        userId
        displayName
        role
        lastReadAt
        isFavorited
        historyStartAt
        hiddenInSidebar
      }
      unreadCount
    }
  }
`;

export const GET_MESSAGES_PAGINATED = gql`
  query GetMessagesPaginated($conversationId: ID!, $cursor: String, $limit: Int) {
    getMessagesPaginated(conversationId: $conversationId, cursor: $cursor, limit: $limit) {
      messages {
        id
        conversationId
        senderId
        senderDisplayName
        content
        role
        threadParentId
        threadReplyCount
        threadLastReplyAt
        reactionCounts
        widgetType
        widgetData
        widgetActionUserId
        isComplete
        createdAt
        editedAt
        deletedAt
        deletedBy
        pinnedAt
        pinnedBy
        forwardedFrom {
          sourceMessageId
          sourceConversationId
          sourceSenderId
          sourceSenderDisplayName
          sourceTimestamp
          sourceContent
        }
        repliedToThreadParentId
        repliedToThreadParentPreview
        alsoSentToChannel
        attachments { key url mimeType sizeBytes fileName kind }
      }
      nextCursor
      hasMore
    }
  }
`;

export const GET_THREAD_REPLIES = gql`
  query GetThreadReplies($messageId: ID!, $cursor: String, $limit: Int) {
    getThreadReplies(messageId: $messageId, cursor: $cursor, limit: $limit) {
      messages {
        id
        conversationId
        senderId
        senderDisplayName
        content
        role
        threadParentId
        reactionCounts
        isComplete
        createdAt
        editedAt
        deletedAt
        deletedBy
        pinnedAt
        pinnedBy
        forwardedFrom {
          sourceMessageId
          sourceConversationId
          sourceSenderId
          sourceSenderDisplayName
          sourceTimestamp
          sourceContent
        }
        repliedToThreadParentId
        repliedToThreadParentPreview
        alsoSentToChannel
        attachments { key url mimeType sizeBytes fileName kind }
      }
      nextCursor
      hasMore
    }
  }
`;

export const GET_CONVERSATION_MEMBERS = gql`
  query GetConversationMembers($conversationId: ID!) {
    getConversationMembers(conversationId: $conversationId) {
      userId
      displayName
      role
      joinedAt
      lastReadAt
    }
  }
`;

export const GET_REACTIONS = gql`
  query GetReactions($messageId: ID!) {
    getReactions(messageId: $messageId) {
      userId
      displayName
      emoji
      messageId
      createdAt
    }
  }
`;

export const GET_CALL_SESSION = gql`
  query GetCallSession($callId: ID!) {
    getCallSession(callId: $callId) {
      callId
      direction
      provider
      status
      fromNumber
      toNumber
      authState
      authUserId
      authUsername
      authDisplayName
      authAttempts
      lockoutUntil
      transferState
      transferReason
      transcriptEnabled
      recordingEnabled
      retentionDays
      createdAt
      updatedAt
      endedAt
    }
  }
`;

export const LIST_RECENT_CALL_SESSIONS = gql`
  ${CALL_SESSION_FRAGMENT}
  query ListRecentCallSessions($limit: Int, $activeOnly: Boolean) {
    listRecentCallSessions(limit: $limit, activeOnly: $activeOnly) {
      ...CallSessionFields
    }
  }
`;

export const LIST_CALL_EVENTS = gql`
  query ListCallEvents($callId: ID!, $limit: Int) {
    listCallEvents(callId: $callId, limit: $limit) {
      callId
      eventId
      eventType
      payload
      createdAt
    }
  }
`;

export const LIST_CALL_TRANSCRIPTS = gql`
  query ListCallTranscripts($callId: ID!, $limit: Int) {
    listCallTranscripts(callId: $callId, limit: $limit) {
      callId
      segmentId
      speaker
      content
      confidence
      isFinal
      sequenceNumber
      retentionUntil
      createdAt
    }
  }
`;

export const LIST_CALL_RECORDINGS = gql`
  query ListCallRecordings($callId: ID!, $limit: Int) {
    listCallRecordings(callId: $callId, limit: $limit) {
      callId
      recordingId
      objectKey
      durationSeconds
      encryptionStatus
      kmsKeyId
      storageBucket
      retentionUntil
      createdAt
      retentionDays
    }
  }
`;

// Audit queries
export const LIST_AUDIT_LOGS = gql`
  query ListAuditLogs($filter: AuditLogFilterInput, $limit: Int, $cursor: String) {
    listAuditLogs(filter: $filter, limit: $limit, cursor: $cursor) {
      items {
        auditId
        username
        displayName
        agent
        action
        toolName
        workflowName
        operationType
        capabilityDomain
        conversationType
        executionPhase
        sourceMessageId
        resultStatus
        confirmationStatus
        requestSummary
        resultSummary
        conversationId
        modifiedData
        timestamp
        durationMs
      }
      nextCursor
    }
  }
`;

export const GET_AUDIT_LOG = gql`
  query GetAuditLog($auditId: ID!) {
    getAuditLog(auditId: $auditId) {
      auditId
      username
      displayName
      agent
      action
      toolName
      workflowName
      operationType
      capabilityDomain
      conversationType
      executionPhase
      sourceMessageId
      resultStatus
      confirmationStatus
      requestSummary
      resultSummary
      conversationId
      modifiedData
      timestamp
      durationMs
    }
  }
`;

export const GET_COST_DASHBOARD = gql`
  query GetCostDashboard($range: CostRangeInput, $filter: CostEventFilterInput) {
    getCostDashboard(range: $range, filter: $filter) {
      range {
        start
        end
        granularity
      }
      totals {
        estimatedCostUsd
        requestCount
        errorCount
        canceledCount
        partialPricingCount
        totalTokens
        inputTokens
        outputTokens
        cachedInputTokens
        reasoningTokens
      }
      trend {
        bucketStart
        estimatedCostUsd
        requestCount
        errorCount
        canceledCount
        partialPricingCount
        totalTokens
      }
      byService {
        key
        estimatedCostUsd
        requestCount
        totalTokens
        errorCount
        canceledCount
        partialPricingCount
      }
      byFeature {
        key
        estimatedCostUsd
        requestCount
        totalTokens
        errorCount
        canceledCount
        partialPricingCount
      }
      byModel {
        key
        estimatedCostUsd
        requestCount
        totalTokens
        errorCount
        canceledCount
        partialPricingCount
      }
      alertState {
        level
        bucketStart
        estimatedCostUsd
        message
      }
    }
  }
`;

export const LIST_COST_EVENTS = gql`
  query ListCostEvents($filter: CostEventFilterInput, $limit: Int, $cursor: String) {
    listCostEvents(filter: $filter, limit: $limit, cursor: $cursor) {
      items {
        eventId
        requestId
        conversationId
        messageId
        userId
        username
        environment
        service
        operation
        feature
        model
        status
        pricingStatus
        createdAt
        durationMs
        inputTokens
        outputTokens
        totalTokens
        cachedInputTokens
        reasoningTokens
        estimatedCostUsd
        actualCostUsd
        currency
      }
      nextCursor
    }
  }
`;

// ── Mutations ───────────────────────────────────────────────────────────────────

export const ARCHIVE_CONVERSATION = gql`
  mutation ArchiveConversation($id: ID!) {
    archiveConversation(id: $id) {
      id
      creatorId
      title
      description
      type
      memberCount
      createdAt
      updatedAt
    }
  }
`;

export const RENAME_CONVERSATION = gql`
  mutation RenameConversation($id: ID!, $title: String!) {
    renameConversation(id: $id, title: $title) {
      id
      title
      type
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      username
      email
      displayName
      roles
      permissions
      authProvider
      createdAt
      updatedAt
      lastLogin
    }
  }
`;

export const UPDATE_USER_ACCESS = gql`
  mutation UpdateUserAccess($input: UpdateUserAccessInput!) {
    updateUserAccess(input: $input) {
      username
      email
      displayName
      roles
      permissions
      authProvider
      createdAt
      updatedAt
      lastLogin
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($username: String!) {
    deleteUser(username: $username)
  }
`;

export const CREATE_CHANNEL = gql`
  mutation CreateChannel($title: String!, $description: String, $memberIds: [String!]!) {
    createChannel(title: $title, description: $description, memberIds: $memberIds) {
      id title type creatorId description memberCount createdAt updatedAt
    }
  }
`;

export const CREATE_DM = gql`
  mutation CreateDM($recipientId: String!) {
    createDM(recipientId: $recipientId) {
      id title type creatorId createdAt updatedAt
    }
  }
`;

export const CREATE_GROUP_DM = gql`
  mutation CreateGroupDM($input: CreateGroupDMInput!) {
    createGroupDM(input: $input) {
      id title type creatorId memberCount createdAt updatedAt
    }
  }
`;

export const CREATE_AI_CONVERSATION = gql`
  mutation CreateAIConversation($title: String) {
    createAIConversation(title: $title) {
      id title type creatorId createdAt updatedAt
    }
  }
`;

export const SEND_MESSAGE_V2 = gql`
  mutation SendMessageV2(
    $conversationId: ID!
    $content: String!
    $threadParentId: ID
    $alsoPostToChannel: Boolean
    $attachments: [AttachmentInput!]
  ) {
    sendMessage(
      conversationId: $conversationId
      content: $content
      threadParentId: $threadParentId
      alsoPostToChannel: $alsoPostToChannel
      attachments: $attachments
    ) {
      id conversationId senderId senderDisplayName content role
      threadParentId threadReplyCount threadLastReplyAt reactionCounts
      widgetType widgetData widgetActionUserId isComplete createdAt editedAt
      deletedAt deletedBy
      pinnedAt pinnedBy
      forwardedFrom {
        sourceMessageId
        sourceConversationId
        sourceSenderId
        sourceSenderDisplayName
        sourceTimestamp
        sourceContent
      }
      repliedToThreadParentId
      repliedToThreadParentPreview
      alsoSentToChannel
      attachments { key url mimeType sizeBytes fileName kind }
    }
  }
`;

// Edit the caller's own text message. Owner-only — the backend rejects
// attempts to edit someone else's message. The edited message is
// re-broadcast on the onNewMessage subscription with editedAt set; clients
// merge by id.
export const EDIT_MESSAGE = gql`
  mutation EditMessage($messageId: ID!, $conversationId: ID!, $content: String!) {
    editMessage(
      messageId: $messageId
      conversationId: $conversationId
      content: $content
    ) {
      id conversationId senderId senderDisplayName content role
      threadParentId threadReplyCount threadLastReplyAt reactionCounts
      widgetType widgetData widgetActionUserId isComplete createdAt editedAt
      deletedAt deletedBy
      pinnedAt pinnedBy
      forwardedFrom {
        sourceMessageId
        sourceConversationId
        sourceSenderId
        sourceSenderDisplayName
        sourceTimestamp
        sourceContent
      }
      repliedToThreadParentId
      repliedToThreadParentPreview
      alsoSentToChannel
      attachments { key url mimeType sizeBytes fileName kind }
    }
  }
`;

// KAP-313 — soft-delete a message. Sender or admin only (backend enforces).
// Returns the updated Message so Apollo cache writeQuery sets deletedAt /
// deletedBy / blank content on the existing cache entry. The onNewMessage
// subscription also broadcasts the updated row.
export const DELETE_MESSAGE = gql`
  mutation DeleteMessage($messageId: ID!, $conversationId: ID!) {
    deleteMessage(messageId: $messageId, conversationId: $conversationId) {
      id conversationId senderId senderDisplayName content role
      threadParentId threadReplyCount threadLastReplyAt reactionCounts
      widgetType widgetData widgetActionUserId isComplete createdAt editedAt
      deletedAt deletedBy
      pinnedAt pinnedBy
      forwardedFrom {
        sourceMessageId
        sourceConversationId
        sourceSenderId
        sourceSenderDisplayName
        sourceTimestamp
        sourceContent
      }
      repliedToThreadParentId
      repliedToThreadParentPreview
      alsoSentToChannel
      attachments { key url mimeType sizeBytes fileName kind }
    }
  }
`;

// KAP-311.1 — repost a message to another conversation with an optional note.
// Handler validates membership in BOTH source and target conversations and
// blanks the optional note's whitespace. The returned Message lives in the
// target conversation and carries a `forwardedFrom` snapshot of the source.
export const FORWARD_MESSAGE = gql`
  mutation ForwardMessage(
    $sourceMessageId: ID!
    $sourceConversationId: ID!
    $targetConversationId: ID!
    $note: String
  ) {
    forwardMessage(
      sourceMessageId: $sourceMessageId
      sourceConversationId: $sourceConversationId
      targetConversationId: $targetConversationId
      note: $note
    ) {
      id conversationId senderId senderDisplayName content role
      threadParentId threadReplyCount threadLastReplyAt reactionCounts
      widgetType widgetData widgetActionUserId isComplete createdAt editedAt
      deletedAt deletedBy
      pinnedAt pinnedBy
      forwardedFrom {
        sourceMessageId
        sourceConversationId
        sourceSenderId
        sourceSenderDisplayName
        sourceTimestamp
        sourceContent
      }
      repliedToThreadParentId
      repliedToThreadParentPreview
      alsoSentToChannel
      attachments { key url mimeType sizeBytes fileName kind }
    }
  }
`;

// KAP-311.2 — pin/unpin a message in its conversation. Both mutations are
// idempotent (the handler short-circuits if the row is already in the target
// state) and return the updated Message so Apollo cache merge-by-id flips
// pinnedAt across every cached query. Subscribers receive the same Message
// via onNewMessage per the schema's @aws_subscribe extension.
export const PIN_MESSAGE = gql`
  mutation PinMessage($messageId: ID!, $conversationId: ID!) {
    pinMessage(messageId: $messageId, conversationId: $conversationId) {
      id conversationId senderId senderDisplayName content role
      threadParentId threadReplyCount threadLastReplyAt reactionCounts
      widgetType widgetData widgetActionUserId isComplete createdAt editedAt
      deletedAt deletedBy
      pinnedAt pinnedBy
      forwardedFrom {
        sourceMessageId
        sourceConversationId
        sourceSenderId
        sourceSenderDisplayName
        sourceTimestamp
        sourceContent
      }
      repliedToThreadParentId
      repliedToThreadParentPreview
      alsoSentToChannel
      attachments { key url mimeType sizeBytes fileName kind }
    }
  }
`;

export const UNPIN_MESSAGE = gql`
  mutation UnpinMessage($messageId: ID!, $conversationId: ID!) {
    unpinMessage(messageId: $messageId, conversationId: $conversationId) {
      id conversationId senderId senderDisplayName content role
      threadParentId threadReplyCount threadLastReplyAt reactionCounts
      widgetType widgetData widgetActionUserId isComplete createdAt editedAt
      deletedAt deletedBy
      pinnedAt pinnedBy
      forwardedFrom {
        sourceMessageId
        sourceConversationId
        sourceSenderId
        sourceSenderDisplayName
        sourceTimestamp
        sourceContent
      }
      repliedToThreadParentId
      repliedToThreadParentPreview
      alsoSentToChannel
      attachments { key url mimeType sizeBytes fileName kind }
    }
  }
`;

// KAP-311.2 — every pinned message in a conversation, oldest pin first.
// Drives the PinnedMessagesBanner above the message list.
export const LIST_PINNED_MESSAGES = gql`
  query ListPinnedMessages($conversationId: ID!) {
    listPinnedMessages(conversationId: $conversationId) {
      id conversationId senderId senderDisplayName content role
      threadParentId threadReplyCount threadLastReplyAt reactionCounts
      widgetType widgetData widgetActionUserId isComplete createdAt editedAt
      deletedAt deletedBy
      pinnedAt pinnedBy
      forwardedFrom {
        sourceMessageId
        sourceConversationId
        sourceSenderId
        sourceSenderDisplayName
        sourceTimestamp
        sourceContent
      }
      repliedToThreadParentId
      repliedToThreadParentPreview
      alsoSentToChannel
      attachments { key url mimeType sizeBytes fileName kind }
    }
  }
`;

// KAP-311.3 — per-user save-for-later. Bookmarks are a denormalized snapshot
// at bookmark-time; the source message can be edited or deleted later and
// the bookmark stays useful. Unbookmark returns Boolean (true) so cache
// updates have to be driven by the mutation's `update` function.
export const BOOKMARK_MESSAGE = gql`
  mutation BookmarkMessage($messageId: ID!, $conversationId: ID!) {
    bookmarkMessage(messageId: $messageId, conversationId: $conversationId) {
      id
      messageId
      conversationId
      conversationTitle
      senderId
      senderDisplayName
      contentExcerpt
      bookmarkedAt
    }
  }
`;

export const UNBOOKMARK_MESSAGE = gql`
  mutation UnbookmarkMessage($messageId: ID!, $conversationId: ID!) {
    unbookmarkMessage(messageId: $messageId, conversationId: $conversationId)
  }
`;

export const LIST_BOOKMARKS = gql`
  query ListBookmarks($cursor: String, $limit: Int) {
    listBookmarks(cursor: $cursor, limit: $limit) {
      items {
        id
        messageId
        conversationId
        conversationTitle
        senderId
        senderDisplayName
        contentExcerpt
        bookmarkedAt
      }
      nextCursor
      hasMore
    }
  }
`;

// Mints a 5-minute presigned PUT URL the client uses to upload a single
// attachment directly to S3. The returned `key` is what gets passed back
// in SendMessageV2's $attachments argument.
export const GET_UPLOAD_URL = gql`
  mutation GetUploadUrl(
    $conversationId: ID!
    $fileName: String!
    $mimeType: String!
    $sizeBytes: Int!
  ) {
    getUploadUrl(
      conversationId: $conversationId
      fileName: $fileName
      mimeType: $mimeType
      sizeBytes: $sizeBytes
    ) {
      uploadUrl
      key
      expiresAt
      kind
      fields
    }
  }
`;

export const ADD_REACTION = gql`
  mutation AddReaction($messageId: ID!, $conversationId: ID!, $emoji: String!) {
    addReaction(messageId: $messageId, conversationId: $conversationId, emoji: $emoji) {
      reactionCounts
    }
  }
`;

export const REMOVE_REACTION = gql`
  mutation RemoveReaction($messageId: ID!, $conversationId: ID!, $emoji: String!) {
    removeReaction(messageId: $messageId, conversationId: $conversationId, emoji: $emoji) {
      reactionCounts
    }
  }
`;

export const MARK_CONVERSATION_READ = gql`
  mutation MarkConversationRead($conversationId: ID!) {
    markConversationRead(conversationId: $conversationId) {
      userId lastReadAt
    }
  }
`;

export const TOGGLE_FAVORITE = gql`
  mutation ToggleFavorite($conversationId: ID!) {
    toggleFavorite(conversationId: $conversationId) {
      userId isFavorited
    }
  }
`;

export const TOGGLE_CONVERSATION_AI = gql`
  mutation ToggleConversationAI($conversationId: ID!) {
    toggleConversationAI(conversationId: $conversationId) {
      id
      title
      type
      aiEnabled
      updatedAt
    }
  }
`;

export const UPDATE_PRESENCE = gql`
  mutation UpdatePresence($presenceOverride: String, $statusMessage: String) {
    updatePresence(presenceOverride: $presenceOverride, statusMessage: $statusMessage) {
      username lastSeenAt presenceOverride statusMessage
    }
  }
`;

export const UPDATE_NOTIFICATION_SOUND_PREFERENCE = gql`
  mutation UpdateNotificationSoundPreference($enabled: Boolean, $soundType: String) {
    updateNotificationSoundPreference(enabled: $enabled, soundType: $soundType) {
      username
      notificationSoundEnabled
      notificationSoundType
      updatedAt
    }
  }
`;

export const UPDATE_COMPOSER_TOOLBAR_VISIBLE = gql`
  mutation UpdateComposerToolbarVisible($visible: Boolean!) {
    updateComposerToolbarVisible(visible: $visible) {
      username
      composerToolbarVisible
      updatedAt
    }
  }
`;

// KAP-310 — persist the per-user quick-react chip trio. Backend enforces
// chips.length === 3 and validates every token against the canonical
// reaction-token-catalog. Returns the full UserProfile so the Apollo cache
// writeback updates every consumer of GetMyProfile in one shot.
export const UPDATE_QUICK_REACT_CHIPS = gql`
  mutation UpdateQuickReactChips($chips: [String!]!) {
    updateQuickReactChips(chips: $chips) {
      username
      email
      displayName
      roles
      permissions
      authProvider
      createdAt
      updatedAt
      lastLogin
      lastSeenAt
      presenceOverride
      statusMessage
      notificationSoundEnabled
      notificationSoundType
      composerToolbarVisible
      quickReactChips
    }
  }
`;

export const ADD_CHANNEL_MEMBERS = gql`
  mutation AddChannelMembers(
    $conversationId: ID!
    $userIds: [String!]!
    $includeHistory: Boolean!
  ) {
    addChannelMembers(
      conversationId: $conversationId
      userIds: $userIds
      includeHistory: $includeHistory
    ) {
      id memberCount
    }
  }
`;

// Per-user soft hide for DMs. Sets historyStartAt=now and
// hiddenInSidebar=true on the caller's own MEMBER row. The peer is
// unaffected. The next incoming message clears hiddenInSidebar so the
// DM resurfaces — historyStartAt persists, so the caller sees only the
// new content. UI must label this "Hide chat", not "Delete chat".
export const DELETE_DIRECT_MESSAGE_FOR_ME = gql`
  mutation DeleteDirectMessageForMe($conversationId: ID!) {
    deleteDirectMessageForMe(conversationId: $conversationId) {
      userId
      historyStartAt
      hiddenInSidebar
    }
  }
`;

export const REMOVE_CHANNEL_MEMBER = gql`
  mutation RemoveChannelMember($conversationId: ID!, $userId: String!) {
    removeChannelMember(conversationId: $conversationId, userId: $userId) {
      id memberCount
    }
  }
`;

export const TRANSFER_CHANNEL_OWNERSHIP = gql`
  mutation TransferChannelOwnership($conversationId: ID!, $newOwnerId: String!) {
    transferChannelOwnership(conversationId: $conversationId, newOwnerId: $newOwnerId) {
      id
    }
  }
`;

export const START_OUTBOUND_CALL = gql`
  ${CALL_SESSION_FRAGMENT}
  mutation StartOutboundCall($input: StartOutboundCallInput!) {
    startOutboundCall(input: $input) {
      ...CallSessionFields
    }
  }
`;

export const SUBMIT_VOICE_AUTH_CHALLENGE = gql`
  mutation SubmitVoiceAuthChallenge($callId: ID!, $pinDigits: String!) {
    submitVoiceAuthChallenge(callId: $callId, pinDigits: $pinDigits) {
      callId
      authenticated
      authState
      attemptsRemaining
      lockoutUntil
      shouldTransferToSupport
      effectivePermissions
      authUserId
      authUsername
      authDisplayName
    }
  }
`;

export const REQUEST_HUMAN_TRANSFER = gql`
  ${CALL_SESSION_FRAGMENT}
  mutation RequestHumanTransfer($callId: ID!, $reason: String) {
    requestHumanTransfer(callId: $callId, reason: $reason) {
      ...CallSessionFields
    }
  }
`;

export const HANGUP_CALL = gql`
  ${CALL_SESSION_FRAGMENT}
  mutation HangupCall($callId: ID!) {
    hangupCall(callId: $callId) {
      ...CallSessionFields
    }
  }
`;

export const ISSUE_VOICE_CLIENT_TOKEN = gql`
  mutation IssueVoiceClientToken($provider: String) {
    issueVoiceClientToken(provider: $provider) {
      token
      expiresAt
      provider
      identityUserId
    }
  }
`;

export const VOICE_REALTIME_EVENT = gql`
  mutation VoiceRealtimeEvent($callId: ID!, $eventType: String!, $payload: AWSJSON, $identityContext: AWSJSON) {
    voiceRealtimeEvent(callId: $callId, eventType: $eventType, payload: $payload, identityContext: $identityContext) {
      callId
      segmentId
      speaker
      content
      confidence
      isFinal
      sequenceNumber
      retentionUntil
      createdAt
    }
  }
`;

export const VOICE_RECORDING_EVENT = gql`
  mutation VoiceRecordingEvent(
    $callId: ID!,
    $recordingId: ID,
    $objectKey: String!,
    $durationSeconds: Int,
    $storageBucket: String,
    $storageProvider: String,
    $encryptionStatus: String,
    $kmsKeyId: String,
    $identityContext: AWSJSON
  ) {
    voiceRecordingEvent(
      callId: $callId,
      recordingId: $recordingId,
      objectKey: $objectKey,
      durationSeconds: $durationSeconds,
      storageBucket: $storageBucket,
      storageProvider: $storageProvider,
      encryptionStatus: $encryptionStatus,
      kmsKeyId: $kmsKeyId,
      identityContext: $identityContext
    ) {
      callId
      recordingId
      objectKey
      durationSeconds
      encryptionStatus
      kmsKeyId
      storageBucket
      retentionUntil
      createdAt
      retentionDays
    }
  }
`;

export const ENFORCE_VOICE_RETENTION = gql`
  mutation EnforceVoiceRetention($limit: Int, $dryRun: Boolean) {
    enforceVoiceRetention(limit: $limit, dryRun: $dryRun) {
      scannedCount
      expiredCount
      deletedTranscriptCount
      deletedRecordingCount
      objectDeleteFailures
      dryRun
      hasMore
    }
  }
`;

export const START_VOICE_REALTIME_SIDEBAND = gql`
  mutation StartVoiceRealtimeSideband($callId: ID!, $durationSeconds: Int) {
    startVoiceRealtimeSideband(callId: $callId, durationSeconds: $durationSeconds) {
      callId
      status
      eventCount
      transcriptCount
      durationMs
    }
  }
`;

// ── Subscriptions ───────────────────────────────────────────────────────────────

export const ON_NEW_MESSAGE = gql`
  subscription OnNewMessage($conversationId: ID!) {
    onNewMessage(conversationId: $conversationId) {
      id
      conversationId
      senderId
      senderDisplayName
      content
      role
      threadParentId
      threadReplyCount
      threadLastReplyAt
      reactionCounts
      widgetType
      widgetData
      widgetActionUserId
      isComplete
      timestamp
      createdAt
      editedAt
      deletedAt
      deletedBy
      pinnedAt
      pinnedBy
      forwardedFrom {
        sourceMessageId
        sourceConversationId
        sourceSenderId
        sourceSenderDisplayName
        sourceTimestamp
        sourceContent
      }
      repliedToThreadParentId
      repliedToThreadParentPreview
      alsoSentToChannel
      attachments { key url mimeType sizeBytes fileName kind }
    }
  }
`;

export const ON_MESSAGE_UPDATE = gql`
  subscription OnMessageUpdate($conversationId: ID!) {
    onMessageUpdate(conversationId: $conversationId) {
      messageId
      conversationId
      content
      isComplete
      timestamp
    }
  }
`;

export const ON_USER_NOTIFICATION = gql`
  subscription OnUserNotification($userId: ID!) {
    onUserNotification(userId: $userId) {
      conversationId messageId senderId senderDisplayName preview threadParentId createdAt
    }
  }
`;

export const ON_PRESENCE_UPDATE = gql`
  subscription OnPresenceUpdate {
    onPresenceUpdate {
      username lastSeenAt presenceOverride statusMessage
    }
  }
`;

export const ON_REACTION_UPDATE = gql`
  subscription OnReactionUpdate($conversationId: ID!) {
    onReactionUpdate(conversationId: $conversationId) {
      id reactionCounts
    }
  }
`;

export const ON_CALL_STATE_UPDATE = gql`
  ${CALL_SESSION_FRAGMENT}
  subscription OnCallStateUpdate($callId: ID!) {
    onCallStateUpdate(callId: $callId) {
      ...CallSessionFields
    }
  }
`;

export const ON_TRANSCRIPT_UPDATE = gql`
  subscription OnTranscriptUpdate($callId: ID!) {
    onTranscriptUpdate(callId: $callId) {
      callId
      segmentId
      speaker
      content
      confidence
      isFinal
      sequenceNumber
      retentionUntil
      createdAt
    }
  }
`;
